/**
 * Equivalent Forms of Rational Numbers generator (6.NR.1.1).
 *
 * Convert a positive rational number between its fraction, decimal, and percent
 * forms. A base rational is chosen first (from SC-allowed denominators so every
 * decimal terminates), then the prompt asks for one target form and the answer
 * is the exact Rational value of that form. Because the grader parses "0.75"
 * and "3/4" to the same Rational, the structured answer matches whichever
 * equivalent form the student enters.
 *
 * Families:
 *   F1: fraction -> decimal
 *   F2: decimal  -> percent
 *   F3: percent  -> fraction
 *   F4: fraction -> percent
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "equivalent-forms" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  /** Denominators permitted at this difficulty (subset of SC_DENOMS). */
  readonly denoms: readonly number[];
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], denoms: [2, 4, 5, 10] },
  standard: { families: ["F1", "F2", "F3", "F4"], denoms: [2, 4, 5, 8, 10, 20, 25] },
  hard: { families: ["F1", "F2", "F3", "F4"], denoms: [8, 20, 25, 50, 100] },
  challenge: { families: ["F2", "F3", "F4"], denoms: [8, 25, 50, 100, 200] },
};

/** Count decimal places needed for a terminating value with denominator d. */
function placesFor(den: number): number {
  // Every SC denominator divides 1000 -> at most 3 decimal places; compute exact.
  let d = den;
  let twos = 0;
  let fives = 0;
  while (d % 2 === 0) {
    d /= 2;
    twos++;
  }
  while (d % 5 === 0) {
    d /= 5;
    fives++;
  }
  return Math.max(twos, fives);
}

export class EquivalentFormsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      const den = rng.pick(cfg.denoms);
      // Proper positive fraction, numerator in [1, den-1].
      const num = rng.int(1, den - 1);
      const value = rat(num, den); // reduced automatically
      const places = placesFor(den);
      const decimalStr = value.toNumber().toFixed(places);
      const percentValue = value.mul(rat(100)); // exact Rational
      const percentDisplay = trimNumber(percentValue.toNumber());

      let promptText: string;
      let answer: Rational;
      let stepDesc: string;
      let stepLatex: string;

      switch (family) {
        case "F1": {
          // fraction -> decimal
          answer = value;
          promptText = `Write ${value.toString()} as a decimal.`;
          stepDesc = "Divide the numerator by the denominator.";
          stepLatex = `${value.toLatex()} = ${decimalStr}`;
          break;
        }
        case "F2": {
          // decimal -> percent (answer is the percent value, e.g. 60 for 60%)
          answer = percentValue;
          promptText = `Write ${decimalStr} as a percent. (Enter the number only.)`;
          stepDesc = "Multiply the decimal by 100 to get the percent.";
          stepLatex = `${decimalStr} \\times 100 = ${percentDisplay}\\%`;
          break;
        }
        case "F3": {
          // percent -> fraction
          answer = value;
          promptText = `Write ${percentDisplay}% as a fraction in simplest form.`;
          stepDesc = "Write the percent over 100, then simplify.";
          stepLatex = `${percentDisplay}\\% = \\frac{${percentDisplay}}{100} = ${value.toLatex()}`;
          break;
        }
        case "F4": {
          // fraction -> percent
          answer = percentValue;
          promptText = `Write ${value.toString()} as a percent. (Enter the number only.)`;
          stepDesc = "Convert to a decimal, then multiply by 100.";
          stepLatex = `${value.toLatex()} = ${decimalStr} = ${percentDisplay}\\%`;
          break;
        }
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText, latex: value.toLatex() },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [{ description: stepDesc, latex: stepLatex }],
        hints: [
          { level: 1, text: "Fractions, decimals, and percents are three ways to write the same value." },
          {
            level: 2,
            text: "Fraction to decimal: divide top by bottom. Decimal to percent: multiply by 100. Percent to fraction: put over 100 and simplify.",
          },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

/** Trim trailing zeros from a number's decimal display, e.g. 60.0 -> "60". */
function trimNumber(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toString();
}

export const equivalentForms = new EquivalentFormsGenerator();
